import { DmObjectType, getDmIcon } from '@bimser/common';
import { BCCheckbox, BCInputMultiLanguage, BCNotification, findMessage } from '@bimser/components';
import { MLHelper } from '@bimser/core';
import DeleteIcon from '@bimser/icons/16/delete';
import _get from 'lodash/get';
import * as React from 'react';
import Style from '../assets/style.scss';
import { IProfileFormFile } from '../entities/IProfileFormFile';
import OnChangeFilePropertyPayload from '../entities/OnChangeFilePropertyPayload';

interface FileListItemProps {
	item: IProfileFormFile;
	isSelected: boolean;
	readOnly?: boolean;
	onFilePropertyChange: (payload: OnChangeFilePropertyPayload) => void;
	onSelectionChange: (id: string, selected: boolean, ctrlKey: boolean) => void;
	onRemoveFile: (fileId: string) => void;
}

const FileListItem = (props: FileListItemProps) => {
	const [isEditMode, setEditMode] = React.useState({ fileName: false, fileDescription: false });
	const editRef = React.useRef<HTMLSpanElement>(null);

	React.useEffect(() => {
		if (props.item.hasValidation) {
			BCNotification.error({
				description: findMessage.get('101984'),
				message: findMessage.get('100926')
			});
		}
	}, [props.item.hasValidation]);

	React.useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleClickOutside = (event: MouseEvent) => {
		const popover = document.querySelector('.ant-popover');
		if (
			editRef.current &&
			!editRef.current.contains(event.target as Node) &&
			!(popover && popover.contains(event.target as Node))
		) {
			setEditMode({ fileName: false, fileDescription: false });
		}
	};

	const onClickRemove = () => {
		props.onRemoveFile(props.item.id);
	};

	const fnStopPropagation = (e: React.MouseEvent) => {
		e.stopPropagation();
	};

	const onClickEdit = (property: string) => (e: React.MouseEvent) => {
		if (!props.readOnly) {
			setEditMode({
				fileDescription: property == 'fileDescription',
				fileName: property == 'fileName'
			});
			e.stopPropagation();
		}
	};

	const onItemPropertyChange = (property: string) => (culture: string, value: string) => {
		const newValue: any = { ..._get(props.item, property) };
		newValue[culture] = value;
		props.onFilePropertyChange({
			id: props.item.id,
			propertyName: property,
			value: newValue
		});
	};

	const renderItemProperty = (property: string, className: string, placeHolder: any = null) => {
		if (_get(isEditMode, property)) {
			return (
				<span
					ref={editRef}
					className={className}
					onClick={fnStopPropagation}
				>
					<BCInputMultiLanguage
						autoFocus
						size="small"
						values={_get(props.item, property)}
						supportedLanguages={MLHelper.supportedLanguages}
						currentLanguage={MLHelper.currentLanguage}
						onLanguageValueChange={onItemPropertyChange(property)}
						placeHolder={placeHolder}
					/>
				</span>
			);
		} else {
			return (
				<span
					className={className}
					onClick={onClickEdit(property)}
					title={MLHelper.getMLText(_get(props.item, property))}
				>
					{MLHelper.getMLText(_get(props.item, property)) || placeHolder}
				</span>
			);
		}
	};

	const getDmObjectType = () => {
		const { documentType } = props.item;

		switch (documentType) {
			case 1:
				return DmObjectType.Folder;
			default:
				return DmObjectType.File;
		}
	};

	return (
		<div
			className={
				Style.profileFormFileItem + ' ' + (props.isSelected ? Style.selectedItem : '')
			}
		>
			<BCCheckbox
				key={props.item.id}
				checked={props.isSelected}
				onChange={(e: React.MouseEvent) => {
					props.onSelectionChange(props.item.id, !props.isSelected, e.ctrlKey);
				}}
				className={Style.itemCheckbox}
			/>
			<div className={Style.itemIcon}>
				{getDmIcon(getDmObjectType(), null, MLHelper.getMLText(props.item.fileName))}
			</div>
			<div className={Style.itemInfo}>
				{renderItemProperty('fileName', Style.itemName, '')}
				{renderItemProperty(
					'fileDescription',
					Style.itemDescription,
					findMessage.get('102307')
				)}
			</div>
			{!props.readOnly ? (
				<div className={Style.itemOptions}>
					<DeleteIcon
						className={Style.optionItem}
						onClick={onClickRemove}
					/>
				</div>
			) : null}
		</div>
	);
};

export default FileListItem;
